import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  CheckIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  ClockIcon,
  UserIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  AcademicCapIcon,
  VideoCameraIcon,
  MicrophoneIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../lib/axiosConfig';
import { clearAiInterviewTakeState } from '../../lib/publicAssessmentFlow';
import { BRAND } from '../jodi/brand';
import { useCandidateLanguage } from '../../i18n/candidate';

/**
 * Interview Complete Page
 * Shows confirmation and next steps to candidates after interview completion.
 * Follows same UI/UX as screening steps (ScreeningApply, AIInterviewJoin, AssessmentResults).
 */
const STEP_SECONDARY = {
  'Basic details': 'Share your contact information',
  'Resume upload': 'Upload your latest resume',
  Assessment: 'Complete skills assessment',
  'AI interview': 'Answer structured questions',
  'Document upload': 'Upload supporting documents (optional)',
};

const getStepMeta = (interviewType) => ({
  'Basic details': {
    icon: UserIcon,
    desc: '',
    gradient: 'from-blue-500 to-indigo-600',
  },
  'Resume upload': {
    icon: DocumentTextIcon,
    desc: '',
    gradient: 'from-electric-blue to-indigo-600',
  },
  Assessment: {
    icon: AcademicCapIcon,
    desc: '',
    gradient: 'from-electric-blue to-indigo-600',
  },
  'AI interview': {
    icon: interviewType === 'video' ? VideoCameraIcon : MicrophoneIcon,
    desc: '',
    gradient: 'from-electric-blue to-indigo-600',
  },
  'Document upload': {
    icon: DocumentTextIcon,
    desc: '',
    gradient: 'from-electric-blue to-indigo-600',
  },
  'Application form': {
    icon: ClipboardDocumentListIcon,
    desc: '',
    gradient: 'from-electric-blue to-indigo-600',
  },
});

function getInterviewStepLabel(interviewType) {
  if (interviewType === 'video') return 'AI video interview';
  if (interviewType === 'voice' || interviewType === 'audio') return 'AI voice interview';
  return 'AI interview';
}

// Arjun (pages/jodi) sends job seekers here for its interview and leaves sessionStorage
// 'arjun.returnTo' (a /jodi/ path) so they can get back. Without that key nothing renders.
function ArjunReturnButton({ t }) {
  const [to] = useState(() => {
    try {
      const value = window.sessionStorage.getItem('arjun.returnTo');
      return typeof value === 'string' && value.startsWith('/jodi/') ? value : null;
    } catch {
      return null;
    }
  });
  if (!to) return null;
  return (
    <div className="mb-5 sm:mb-8 text-center">
      <a
        href={to}
        onClick={() => {
          try {
            window.sessionStorage.removeItem('arjun.returnTo');
          } catch {
            // Nothing stored.
          }
        }}
        className="inline-flex items-center justify-center gap-2 w-full sm:w-auto rounded-lg bg-gray-900 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 transition-colors sm:text-base"
      >
        {t('Back to {name}', { name: BRAND.talentAgent })}
        <ArrowRightIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" aria-hidden="true" />
      </a>
      <p className="mt-2 text-xs text-gray-500 sm:text-sm">
        {t('Your results appear there in a minute or two.')}
      </p>
    </div>
  );
}

const InterviewComplete = () => {
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const candidateName = state?.candidateName || 'Candidate';
  const candidateEmail = state?.email || searchParams.get('email') || '';
  const sessionId = state?.sessionId;
  const sessionToken = state?.sessionToken;
  const hasAssessment = state?.hasAssessment === true;
  // screeningId from state (navigate) or from URL (fallback when state is lost e.g. refresh)
  const screeningId = state?.screeningId || searchParams.get('screeningId') || null;
  const interviewTypeParam = searchParams.get('interviewType');
  // Interview is always the last step when in screening flow (interview-only or assessment+interview)
  const isLastScreeningStep = Boolean(screeningId);
  const [hasResumeScoring, setHasResumeScoring] = useState(screeningId ? null : false);
  const [resumeStepResolved, setResumeStepResolved] = useState(!screeningId);
  const [applyInfo, setApplyInfo] = useState(null);
  const t = useCandidateLanguage({ screeningId });

  useEffect(() => {
    clearAiInterviewTakeState();
  }, []);

  // Block back button - interview cannot be restarted
  useEffect(() => {
    const blockBack = () => {
      window.history.pushState(null, '', window.location.href);
    };
    window.addEventListener('popstate', blockBack);
    window.history.pushState(null, '', window.location.href);
    return () => window.removeEventListener('popstate', blockBack);
  }, []);

  // Immediately call /complete to ensure egress recording is stopped and
  // session is finalized. The agent's shutdown callback may also call this
  // endpoint — the backend handles duplicate calls gracefully.
  useEffect(() => {
    if (!sessionId) return;
    const headers = { 'Content-Type': 'application/json' };
    if (sessionToken) headers['x-session-token'] = sessionToken;
    headers['x-completion-source'] = 'client';
    fetch(`${API_BASE_URL}/api/ai-interviews/session/${sessionId}/complete`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ reason: 'interview_ended', summary: '' }),
    })
      .then((res) => {
        if (res.ok) return;
        return res.text().then((t) => Promise.reject(new Error(t)));
      })
      .catch((err) => {
        console.debug('InterviewComplete: complete request:', err.message);
      });
  }, [sessionId, sessionToken]);

  // Load screening config (resume is optional per screening).
  useEffect(() => {
    if (!screeningId) {
      setHasResumeScoring(false);
      setResumeStepResolved(true);
      return;
    }
    let cancelled = false;
    setResumeStepResolved(false);
    const fetchApplyInfo = async () => {
      try {
        const params = candidateEmail ? `?email=${encodeURIComponent(candidateEmail)}` : '';
        const res = await axios.get(`/api/screenings/${screeningId}/apply-info${params}`);
        if (!cancelled) {
          setApplyInfo(res.data);
          setHasResumeScoring(res.data?.hasResumeScoring !== false);
          setResumeStepResolved(true);
        }
      } catch {
        if (!cancelled) {
          setApplyInfo(null);
          setHasResumeScoring(false);
          setResumeStepResolved(true);
        }
      }
    };
    fetchApplyInfo();
    return () => {
      cancelled = true;
    };
  }, [screeningId, candidateEmail]);

  const includeDocumentCollection = !!applyInfo?.includeDocumentCollection;
  const includeApplicationForm = !!applyInfo?.includeApplicationForm;
  const documentCollectionToken = applyInfo?.documentCollectionToken || null;
  const documentCollectionCompleted = !!applyInfo?.documentCollectionCompleted;
  const resolvedCandidateName = applyInfo?.existingCandidate?.name || state?.candidateName || searchParams.get('name') || 'Candidate';

  const isStepCompleted = (stepName) => {
    if (stepName === 'Document upload') return documentCollectionCompleted;
    return true; // All steps up to AI interview are completed
  };

  const includeResumeStep = hasResumeScoring && resumeStepResolved;
  const steps = [
    'Basic details',
    ...(includeResumeStep ? ['Resume upload'] : []),
    ...(includeDocumentCollection ? ['Document upload'] : []),
    ...(includeApplicationForm ? ['Application form'] : []),
    ...(hasAssessment ? ['Assessment'] : []),
    'AI interview',
  ];
  
  const normalizedInterviewType = (state?.interviewType || interviewTypeParam || '').toLowerCase();
  const effectiveInterviewType =
    normalizedInterviewType === 'video'
      ? 'video'
      : normalizedInterviewType === 'voice' || normalizedInterviewType === 'audio'
        ? 'voice'
        : null;
  const stepMetaMap = getStepMeta(effectiveInterviewType || 'voice');

  return (
    <div className="min-h-screen bg-white-smoke px-4 py-4 sm:py-6 md:py-8">
      <div className="mx-auto max-w-6xl">
        {/* Header: compact on mobile */}
        <header className="flex items-center justify-between gap-3">
          <div className="flex flex-col min-w-0">
            <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              {t('Powered by')}
            </p>
            <div className="mt-0.5 flex items-center gap-1.5">
              <img
                src="/logo.svg"
                alt="TalentCrux"
                className="h-9 w-9 sm:h-12 sm:w-12 md:h-14 md:w-14 object-contain flex-shrink-0"
              />
              <span className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 truncate">TalentCrux</span>
            </div>
          </div>
          <div className="hidden sm:flex items-center justify-end flex-shrink-0 min-w-0" aria-hidden />
        </header>

        {/* Screening steps stepper – themed to match other pages */}
        {steps.length > 0 && (
          <section className="mt-6 sm:mt-8">
            <p className="text-center text-[11px] sm:text-sm font-semibold uppercase tracking-[0.22em] text-gray-500 mb-5 sm:mb-7">
              {t('Your screening journey')}
            </p>
            <div className="flex items-start justify-center gap-0">
              {steps.map((label, index) => {
                const meta =
                  stepMetaMap[label] || {
                    icon: CheckIcon,
                    desc: STEP_SECONDARY[label] || '',
                    gradient: 'from-gray-400 to-gray-500',
                  };
                const StepIcon = meta.icon;
                
                const isActive = label === 'Document upload' && includeDocumentCollection && !documentCollectionCompleted;
                const isCompleted = isStepCompleted(label);
                
                return (
                  <React.Fragment key={label}>
                    <div className="flex flex-col items-center text-center min-w-0 flex-1 sm:flex-none sm:w-32">
                      <span
                        className={`text-[9px] sm:text-xs font-semibold uppercase tracking-[0.06em] sm:tracking-[0.16em] whitespace-nowrap mb-1.5 ${
                          isActive ? 'text-electric-blue' : isCompleted ? 'text-emerald-400' : 'text-gray-300'
                        }`}
                      >
                        {t('Step {number}', { number: index + 1 })}
                      </span>
                      <div
                        className={`relative flex h-12 w-12 sm:h-16 sm:w-16 items-center justify-center rounded-2xl transition-all duration-500 ${
                          isCompleted
                            ? 'bg-emerald-50 text-emerald-500 border-2 border-emerald-200'
                            : isActive
                            ? `bg-gradient-to-br ${meta.gradient} text-white shadow-lg`
                            : 'bg-white text-gray-300 border-2 border-gray-200 border-dashed'
                        }`}
                        style={
                          isCompleted
                            ? { boxShadow: '0 6px 18px rgba(16, 185, 129, 0.18)' }
                            : isActive
                            ? { boxShadow: '0 8px 25px rgba(0, 123, 255, 0.2)' }
                            : {}
                        }
                      >
                        <StepIcon className="h-5 w-5 sm:h-7 sm:w-7" strokeWidth={isActive || isCompleted ? 2 : 1.5} />
                        {isActive && (
                          <span
                            className="absolute inset-0 rounded-2xl border-2 border-electric-blue/30 animate-ping"
                            style={{ animationDuration: '2s' }}
                          />
                        )}
                      </div>
                      <p
                        className={`mt-2 text-[10px] sm:text-sm font-semibold leading-tight hyphens-auto [overflow-wrap:anywhere] ${
                          isCompleted ? 'text-emerald-600' : isActive ? 'text-gray-900' : 'text-gray-400'
                        }`}
                      >
                        {t(label === 'AI interview'
                          ? getInterviewStepLabel(effectiveInterviewType || undefined)
                          : label)}
                      </p>
                    </div>
                    {index < steps.length - 1 && (
                      <div className="flex-1 flex items-center pt-7 sm:pt-8 min-w-[6px] max-w-[12px] sm:max-w-[72px]">
                        <div className="w-full flex items-center gap-0.5">
                          {[...Array(4)].map((_, i) => {
                            const isLineCompleted = isStepCompleted(label) && isStepCompleted(steps[index + 1]);
                            return (
                              <div
                                key={i}
                                className={`flex-1 h-0.5 rounded-full ${
                                  isLineCompleted ? 'bg-emerald-400/60' : 'bg-gray-200'
                                }`}
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </section>
        )}

        {/* Main card – wider layout to match other steps */}
        <div className="mt-4 sm:mt-6 max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-6 lg:p-8">
            <h1 className="text-lg font-bold text-gray-900 text-center mb-5 sm:mb-8 sm:text-xl lg:text-2xl">
              {t('Interview Complete!')}
            </h1>

            <ArjunReturnButton t={t} />

            {/* Next steps summary – neutral brand card (not green) */}
            <div className="rounded-xl border border-electric-blue-100 bg-electric-blue-50 px-3 py-4 sm:px-5 sm:py-5 mb-5 sm:mb-8">
              <h2 className="text-sm font-semibold text-gray-900 mb-3 sm:mb-4 text-center sm:text-base">
                {t('What happens next?')}
              </h2>
              <div className="space-y-3 sm:space-y-4 text-left">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="flex h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 items-center justify-center rounded-full bg-electric-blue text-xs sm:text-sm font-semibold text-white">
                    1
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold text-gray-900 sm:text-sm">{t('Review process')}</h3>
                    <p className="text-xs text-gray-700 mt-0.5 sm:text-sm">
                      {t('Our team will carefully review your interview responses.')}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="flex h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 items-center justify-center rounded-full bg-electric-blue text-xs sm:text-sm font-semibold text-white">
                    2
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold text-gray-900 sm:text-sm">{t('Evaluation')}</h3>
                    <p className="text-xs text-gray-700 mt-0.5 sm:text-sm">
                      {t('Your responses will be evaluated against the role requirements.')}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="flex h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 items-center justify-center rounded-full bg-electric-blue text-xs sm:text-sm font-semibold text-white">
                    3
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold text-gray-900 sm:text-sm">{t('Next steps')}</h3>
                    <p className="text-xs text-gray-700 mt-0.5 sm:text-sm">
                      {t("We'll contact you with next steps once the review is complete.")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 lg:gap-4 mb-4 sm:mb-5">
              <div className="flex items-start gap-2 sm:gap-2.5 rounded-lg border border-gray-100 bg-gray-50/80 px-2.5 py-2 sm:px-3 sm:py-2.5">
                <div className="flex h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 items-center justify-center rounded-lg bg-electric-blue-100">
                  <EnvelopeIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-electric-blue" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-gray-900 sm:text-xs">{t('Check your inbox')}</p>
                  <p className="text-[10px] text-gray-600 leading-snug mt-0.5 sm:text-[11px]">
                    {t("We'll email you if any additional details are needed.")}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2 sm:gap-2.5 rounded-lg border border-gray-100 bg-gray-50/80 px-2.5 py-2 sm:px-3 sm:py-2.5">
                <div className="flex h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 items-center justify-center rounded-lg bg-lavender-purple-100">
                  <CheckCircleIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-lavender-purple" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-gray-900 sm:text-xs">{t('Interview recorded')}</p>
                  <p className="text-[10px] text-gray-600 leading-snug mt-0.5 sm:text-[11px]">
                    {t('Your conversation has been securely saved for the hiring team.')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2 sm:gap-2.5 rounded-lg border border-gray-100 bg-gray-50/80 px-2.5 py-2 sm:px-3 sm:py-2.5">
                <div className="flex h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 items-center justify-center rounded-lg bg-electric-blue-100">
                  <ClockIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-electric-blue" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-gray-900 sm:text-xs">{t('Timeline')}</p>
                  <p className="text-[10px] text-gray-600 leading-snug mt-0.5 sm:text-[11px]">
                    {t('Most candidates hear back within a few business days.')}
                  </p>
                </div>
              </div>
            </div>

            {isLastScreeningStep && (
              <div className="text-center mb-3 sm:mb-4">
                  <button
                    type="button"
                    onClick={() => {
                      const params = new URLSearchParams();
                      if (candidateEmail) params.set('email', candidateEmail);
                      const qs = params.toString();
                      navigate(`/apply/${screeningId}/thank-you${qs ? `?${qs}` : ''}`, { replace: true });
                    }}
                    className="inline-flex items-center justify-center gap-2 w-full sm:w-auto rounded-lg bg-electric-blue px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-electric-blue-600 focus:outline-none focus:ring-2 focus:ring-electric-blue focus:ring-offset-2 focus:ring-offset-white transition-colors sm:px-6 sm:py-3 cursor-pointer"
                  >
                    <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                    {t('Complete Screening')}
                  </button>
              </div>
            )}

            <div className="pt-3 sm:pt-4 border-t border-gray-200">
              <p className="text-[11px] text-gray-500 text-center sm:text-xs">
                {t('If you have any questions, please reach out at the email provided in your invitation, or')}{' '}
                <a
                  href="mailto:admin@indaratech.net"
                  className="text-electric-blue hover:text-electric-blue-600 hover:underline"
                >
                  {t('contact support')}
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewComplete;
